import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { DOCUMENT_CATEGORIES_CONTENT } from '@/utils/documentCategories';
import { safeFormatDate } from '@/utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Upload, File, Activity, Save, MessageSquare, UserPlus, CheckCircle, Circle, Download, FileCheck, Trash2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';

// Category Dropzone Component
const CategoryDropzone = ({ category, onUpload }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => onUpload(files, category),
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    }
  });

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
        isDragActive
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50'
      }`}
    >
      <input {...getInputProps()} />
      <Upload className={`h-10 w-10 mx-auto mb-3 ${isDragActive ? 'text-blue-500' : 'text-gray-400'}`} />
      {isDragActive ? (
        <p className="text-blue-600 font-medium">Déposez les fichiers ici...</p>
      ) : (
        <>
          <p className="text-gray-700 font-medium mb-1">
            Glissez-déposez vos fichiers ici
          </p>
          <p className="text-sm text-gray-500">
            ou cliquez pour sélectionner (PDF, Images, Word)
          </p>
        </>
      )}
    </div>
  );
};

const FileDetail = () => {
  const { fileId } = useParams();
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [comments, setComments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [newComment, setNewComment] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignClientDialogOpen, setAssignClientDialogOpen] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('administratif');
  
  // États pour les tâches
  const [tasks, setTasks] = useState([]);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({
    employee_id: '',
    title: '',
    description: '',
    priority: 'medium',
    deadline: ''
  });
  
  // États pour les paiements
  const [payments, setPayments] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    // ✅ Create AbortController for cleanup
    const abortController = new AbortController();
    const signal = abortController.signal;
    
    // Flag to track if component is mounted
    let isMounted = true;
    
    // Fetch functions with abort signal
    const fetchAllData = async () => {
      try {
        await Promise.all([
          fetchFileDetails(signal, isMounted),
          fetchDocuments(signal, isMounted),
          fetchActivities(signal, isMounted),
          fetchComments(signal, isMounted),
          fetchCategories(signal, isMounted),
          fetchTasks(signal, isMounted),
          fetchPayments(signal, isMounted),
          ...(user?.role === 'admin' || user?.role === 'employee' 
            ? [fetchEmployees(signal, isMounted), fetchClients(signal, isMounted)]
            : []
          )
        ]);
      } catch (error) {
        // Ignore abort errors
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Error fetching data:', error);
        }
      }
    };
    
    fetchAllData();
    
    // ✅ Cleanup function to abort ongoing requests
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [fileId]);

  const fetchFileDetails = async (signal, isMounted = true) => {
    try {
      const response = await axios.get(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal // ✅ Pass abort signal
      });
      if (isMounted) {
        setFile(response.data);
        setSelectedStatus(response.data.status);
        setSelectedEmployees(response.data.assigned_employees || []);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        toast.error(t('error'));
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const fetchEmployees = async (signal, isMounted = true) => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMounted) {
        const empList = response.data.filter(u => u.role === 'employee');
        setEmployees(empList);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch employees:', error);
      }
    }
  };

  const fetchDocuments = async (signal, isMounted = true) => {
    try {
      const response = await axios.get(`${API}/documents/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMounted) {
        setDocuments(response.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch documents:', error);
      }
    }
  };

  const fetchActivities = async (signal, isMounted = true) => {
    try {
      const response = await axios.get(`${API}/activities/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMounted) {
        setActivities(response.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch activities:', error);
      }
    }
  };

  const fetchComments = async (signal, isMounted = true) => {
    try {
      const response = await axios.get(`${API}/comments/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMounted) {
        setComments(response.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch comments:', error);
      }
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await axios.put(
        `${API}/files/${fileId}`,
        { status: selectedStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Statut mis à jour');
      fetchFileDetails();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const fetchClients = async (signal, isMounted = true) => {
    try {
      const response = await axios.get(`${API}/users?role_filter=client`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMounted) {
        setClients(response.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch clients:', error);
      }
    }
  };

  const handleAssignClient = async () => {
    if (!selectedClientId) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    try {
      await axios.put(
        `${API}/files/${fileId}/assign-client?client_id=${selectedClientId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Client assigné avec succès');
      setAssignClientDialogOpen(false);
      fetchFileDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    }
  };

  const handleAssignEmployees = async () => {
    try {
      await axios.put(
        `${API}/files/${fileId}/assign`,
        selectedEmployees,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Employés assignés avec succès');
      setAssignDialogOpen(false);
      fetchFileDetails();
      fetchActivities();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const fetchCategories = async (signal, isMounted = true) => {
    try {
      const response = await axios.get(`${API}/document-categories`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMounted) {
        setCategories(response.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Failed to fetch categories');
      }
    }
  };

  const handleToggleCheck = async (documentId, currentStatus) => {
    try {
      await axios.put(
        `${API}/documents/${documentId}/check`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { checked: !currentStatus }
        }
      );
      fetchDocuments();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getDocumentsByCategory = (category) => {
    return documents.filter(doc => !doc.is_contract && doc.category === category);
  };

  // Fonctions pour les tâches
  const fetchTasks = async (signal, isMounted = true) => {
    try {
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { file_id: fileId },
        signal
      });
      if (isMounted) {
        setTasks(response.data || []);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Erreur lors du chargement des tâches:', error);
      }
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.employee_id || !newTask.title) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.post(
        `${API}/tasks`,
        {
          ...newTask,
          file_id: fileId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Tâche créée avec succès');
      setTaskDialogOpen(false);
      setNewTask({
        employee_id: '',
        title: '',
        description: '',
        priority: 'medium',
        deadline: ''
      });
      fetchTasks();
      fetchActivities();
    } catch (error) {
      toast.error('Erreur lors de la création de la tâche');
    }
  };

  const handleToggleTaskStatus = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'termine' ? 'en_cours' : 'termine';
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Statut de la tâche mis à jour');
      fetchTasks();
      fetchActivities();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette tâche ?')) return;
    
    try {
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tâche supprimée');
      fetchTasks();
      fetchActivities();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getTaskProgress = () => {
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter(t => t.status === 'termine').length;
    return Math.round((completedTasks / tasks.length) * 100);
  };
  
  // Fonctions pour les paiements
  const fetchPayments = async (signal, isMounted = true) => {
    try {
      const response = await axios.get(`${API}/payments`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { file_id: fileId },
        signal
      });
      
      if (isMounted) {
        // Filtrer les paiements pour ce dossier spécifique
        const filePayments = response.data.filter(p => p.file_id === fileId);
        setPayments(filePayments || []);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Erreur lors du chargement des paiements:', error);
      }
    }
  };
  
  const handleMarkPaymentPaid = async (paymentId) => {
    const paymentMethod = window.prompt('Méthode de paiement (virement, carte, especes, cheque):');
    if (!paymentMethod) return;

    const reference = window.prompt('Numéro de référence (optionnel):');

    try {
      await axios.post(`${API}/payments/${paymentId}/mark-paid`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { payment_method: paymentMethod, reference: reference || undefined }
      });
      toast.success('Paiement marqué comme payé et reçu généré automatiquement! ✅');
      fetchPayments();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };
  
  const getPaymentProgress = () => {
    if (payments.length === 0) return 0;
    const paidPayments = payments.filter(p => p.status === 'paye').length;
    return Math.round((paidPayments / payments.length) * 100);
  };
  
  const getTotalPaid = () => {
    return payments.filter(p => p.status === 'paye').reduce((sum, p) => sum + p.amount, 0);
  };
  
  const getTotalPending = () => {
    return payments.filter(p => p.status !== 'paye').reduce((sum, p) => sum + p.amount, 0);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await axios.post(
        `${API}/comments`,
        { file_id: fileId, content: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Commentaire ajouté');
      setNewComment('');
      fetchComments();
      fetchActivities();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleToggleStep = async (stepKey, currentValue) => {
    try {
      const response = await axios.put(
        `${API}/files/${fileId}/progress`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { step_key: stepKey, completed: !currentValue }
        }
      );
      toast.success('Progression mise à jour');
      fetchFileDetails();
      fetchActivities();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getProgressPercentage = () => {
    // Utiliser la progression basée sur les tâches si des tâches existent
    if (tasks && tasks.length > 0) {
      return getTaskProgress();
    }
    // Sinon, utiliser les étapes de progression traditionnelles
    if (!file || !file.progress_steps) return 0;
    const steps = Object.values(file.progress_steps);
    const completed = steps.filter(Boolean).length;
    return Math.round((completed / steps.length) * 100);
  };

  const progressSteps = [
    { key: 'signature_contrat', label: 'Signature du contrat initial / Consultation initiale' },
    { key: 'ouverture_dossier', label: 'Ouverture du dossier et signature du contrat de services' },
    { key: 'collecte_documents', label: 'Collecte et vérification des documents' },
    { key: 'analyse_juridique', label: 'Analyse juridique et élaboration de la stratégie' },
    { key: 'preparation_formulaires', label: 'Préparation et remplissage des formulaires' },
    { key: 'redaction_lettre', label: 'Rédaction de la lettre de soumission' },
    { key: 'soumission_electronique', label: 'Soumission électronique du dossier' },
    { key: 'suivi_post_soumission', label: 'Suivi post-soumission et communication avec le client' },
    { key: 'reception_decision', label: 'Réception et analyse de la décision' },
    { key: 'cloture_archivage', label: 'Clôture et archivage du dossier' }
  ];

  // Upload function for specific category
  const handleUploadForCategory = async (acceptedFiles, category) => {
    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_id', fileId);
      formData.append('category', category);

      try {
        await axios.post(`${API}/documents/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success(`${file.name} téléversé avec succès`);
        fetchDocuments();
        fetchActivities();
      } catch (error) {
        toast.error(`Erreur: ${file.name}`);
      }
    }
  };

  // Create dropzone for a specific category
  const createCategoryDropzone = (category) => {
    return {
      onDrop: (files) => handleUploadForCategory(files, category),
      accept: {
        'application/pdf': ['.pdf'],
        'image/*': ['.png', '.jpg', '.jpeg'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
      },
      noClick: false,
      noKeyboard: false
    };
  };

  const handleDownloadContract = async (documentId, filename) => {
    try {
      const response = await axios.get(
        `${API}/documents/${documentId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Document téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleUploadSignedContract = async (documentId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.put(
        `${API}/documents/${documentId}/upload-signed`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Contrat signé téléversé avec succès');
      fetchDocuments();
      fetchActivities();
    } catch (error) {
      toast.error('Erreur lors du téléversement');
    }
  };

  const handleDeleteDocument = async (documentId, documentName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${documentName}" ? Cette action est irréversible.`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/documents/${documentId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Document supprimé avec succès');
      fetchDocuments();
      fetchActivities();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas la permission de supprimer ce document');
      } else {
        toast.error('Erreur lors de la suppression du document');
      }
    }
  };


  const getContractName = (docType) => {
    const names = {
      'contrat_initial': 'Contrat de Consultation Initial',
      'contrat_service': 'Contrat de Service Professionnel',
      'recu': 'Reçu de Paiement'
    };
    return names[docType] || docType;
  };

  const getStatusColor = (status) => {
    const colors = {
      ouvert: 'bg-blue-100 text-blue-800 border-blue-200',
      en_cours: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      soumission_ircc: 'bg-purple-100 text-purple-800 border-purple-200',
      en_traitement: 'bg-orange-100 text-orange-800 border-orange-200',
      decision: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      ferme: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusProgress = (status) => {
    const progress = {
      ouvert: 16,
      en_cours: 33,
      soumission_ircc: 50,
      en_traitement: 66,
      decision: 83,
      ferme: 100
    };
    return progress[status] || 0;
  };

  const toggleEmployeeSelection = (empId) => {
    if (selectedEmployees.includes(empId)) {
      setSelectedEmployees(selectedEmployees.filter(id => id !== empId));
    } else {
      setSelectedEmployees([...selectedEmployees, empId]);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="file-detail-container">
        {/* Header */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex-1">
                <CardTitle className="text-3xl mb-2">{file?.file_number}</CardTitle>
                <p className="text-lg text-gray-600">{file?.client_name}</p>
                
                {/* Progress bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium">Progression</span>
                    <span className="text-gray-600">{getStatusProgress(file?.status)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-red-600 to-blue-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${getStatusProgress(file?.status)}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <Badge className={`${getStatusColor(file?.status)} border text-base px-4 py-2`}>
                  {t(file?.status)}
                </Badge>
                {(user?.role === 'admin' || user?.role === 'employee') && (
                  <>
                    {/* Assign Client Dialog */}
                    <Dialog open={assignClientDialogOpen} onOpenChange={setAssignClientDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="border-green-500 text-green-600 hover:bg-green-50">
                          <UserPlus className="h-4 w-4 mr-2" />
                          Assigner Client
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Assigner un client au dossier</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          {file?.client_name && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <p className="text-sm text-blue-800">
                                <strong>Client actuel :</strong> {file.client_name}
                              </p>
                            </div>
                          )}
                          <div className="space-y-2">
                            <Label>Sélectionner un nouveau client</Label>
                            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                              <SelectTrigger>
                                <SelectValue placeholder="Choisir un client" />
                              </SelectTrigger>
                              <SelectContent>
                                {clients.map((client) => (
                                  <SelectItem key={client.id} value={client.id}>
                                    {client.full_name} ({client.email})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            onClick={handleAssignClient}
                            className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
                          >
                            Confirmer l'assignation
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Assign Employees Dialog */}
                    {user?.role === 'admin' && (
                      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" data-testid="assign-employees-btn">
                            <UserPlus className="h-4 w-4 mr-2" />
                            Assigner employés
                          </Button>
                        </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Assigner des employés au dossier</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {employees.map((emp) => (
                          <div
                            key={emp.id}
                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                              selectedEmployees.includes(emp.id)
                                ? 'bg-blue-50 border-blue-500'
                                : 'hover:bg-gray-50'
                            }`}
                            onClick={() => toggleEmployeeSelection(emp.id)}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={selectedEmployees.includes(emp.id)}
                                onChange={() => toggleEmployeeSelection(emp.id)}
                                className="h-4 w-4"
                              />
                              <div>
                                <p className="font-medium">{emp.full_name}</p>
                                <p className="text-sm text-gray-600">{emp.email}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button
                        onClick={handleAssignEmployees}
                        className="w-full bg-gradient-to-r from-red-600 to-blue-600"
                      >
                        Confirmer l'assignation
                      </Button>
                    </DialogContent>
                  </Dialog>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">{t('created_at')}</p>
                <p className="text-lg font-semibold">
                  {safeFormatDate(file?.created_at)}
                </p>
              </div>
              {(user?.role === 'admin' || user?.role === 'employee') && (
                <div className="flex gap-2">
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger data-testid="status-update-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ouvert">{t('ouvert')}</SelectItem>
                      <SelectItem value="en_cours">{t('en_cours')}</SelectItem>
                      <SelectItem value="soumission_ircc">{t('soumission_ircc')}</SelectItem>
                      <SelectItem value="en_traitement">{t('en_traitement')}</SelectItem>
                      <SelectItem value="decision">{t('decision')}</SelectItem>
                      <SelectItem value="ferme">{t('ferme')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleUpdateStatus}
                    disabled={selectedStatus === file?.status}
                    data-testid="save-status-btn"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {t('save')}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="progress" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="progress" data-testid="progress-tab">
              <CheckCircle className="mr-2 h-4 w-4" />
              Progression ({getProgressPercentage()}%)
            </TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tasks-tab">
              <Activity className="mr-2 h-4 w-4" />
              Tâches
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="payments-tab">
              <DollarSign className="mr-2 h-4 w-4" />
              Paiements ({payments.length})
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="documents-tab">
              <File className="mr-2 h-4 w-4" />
              {t('documents')}
            </TabsTrigger>
            <TabsTrigger value="comments" data-testid="comments-tab">
              <MessageSquare className="mr-2 h-4 w-4" />
              Commentaires ({comments.length})
            </TabsTrigger>
            <TabsTrigger value="activities" data-testid="activities-tab">
              <Activity className="mr-2 h-4 w-4" />
              {t('activities')}
            </TabsTrigger>
          </TabsList>

          {/* Progress Tab */}
          <TabsContent value="progress" className="space-y-6">
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Suivi de Progression du Dossier</CardTitle>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-blue-600">
                      {getProgressPercentage()}%
                    </div>
                    <p className="text-sm text-gray-500">Complété</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {progressSteps.map((step, index) => {
                    const isCompleted = file?.progress_steps?.[step.key] || false;
                    const canEdit = user?.role === 'admin' || user?.role === 'employee';
                    
                    return (
                      <div
                        key={step.key}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          isCompleted 
                            ? 'border-green-500 bg-green-50' 
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 mt-1">
                            {canEdit ? (
                              <input
                                type="checkbox"
                                checked={isCompleted}
                                onChange={() => handleToggleStep(step.key, isCompleted)}
                                className="w-6 h-6 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                              />
                            ) : (
                              <div className={`w-6 h-6 rounded flex items-center justify-center ${
                                isCompleted ? 'bg-green-600' : 'bg-gray-300'
                              }`}>
                                {isCompleted && (
                                  <CheckCircle className="h-4 w-4 text-white" />
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className={`font-medium ${isCompleted ? 'text-green-900' : 'text-gray-900'}`}>
                                  {index + 1}. {step.label}
                                </p>
                                {isCompleted && (
                                  <p className="text-sm text-green-600 mt-1">✓ Étape complétée</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Progress Bar */}
                <div className="mt-6 p-4 bg-gradient-to-r from-red-50 to-blue-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Progression globale</span>
                    <span className="text-sm font-bold">{getProgressPercentage()}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className="bg-gradient-to-r from-red-600 to-blue-600 h-4 rounded-full transition-all duration-500"
                      style={{ width: `${getProgressPercentage()}%` }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="space-y-6">
            <Card className="border-0 shadow-xl bg-gradient-to-br from-orange-50 to-red-50">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-6 w-6 text-orange-600" />
                    Gestion des Tâches du Dossier
                  </CardTitle>
                  {(user?.role === 'admin' || user?.role === 'employee') && (
                    <Button onClick={() => setTaskDialogOpen(true)}>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Créer une tâche
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Progression basée sur les tâches */}
                <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border-2 border-orange-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Progression du dossier (basée sur les tâches)</span>
                    <span className="text-2xl font-bold text-orange-600">{getTaskProgress()}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className="bg-gradient-to-r from-orange-600 to-red-600 h-4 rounded-full transition-all duration-500"
                      style={{ width: `${getTaskProgress()}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    {tasks.filter(t => t.status === 'termine').length} sur {tasks.length} tâche(s) complétée(s)
                  </p>
                </div>

                {/* Liste des tâches */}
                <div className="space-y-3">
                  {tasks.length > 0 ? (
                    tasks.map((task) => {
                      const isCompleted = task.status === 'termine';
                      const priorityColors = {
                        low: 'bg-blue-100 text-blue-800',
                        medium: 'bg-yellow-100 text-yellow-800',
                        high: 'bg-orange-100 text-orange-800',
                        urgent: 'bg-red-100 text-red-800'
                      };

                      return (
                        <Card key={task.id} className={`border-2 ${isCompleted ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              {(user?.role === 'admin' || user?.role === 'employee') && (
                                <input
                                  type="checkbox"
                                  checked={isCompleted}
                                  onChange={() => handleToggleTaskStatus(task.id, task.status)}
                                  className="w-5 h-5 text-green-600 rounded cursor-pointer mt-1"
                                />
                              )}
                              <div className="flex-1">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <h4 className={`font-semibold ${isCompleted ? 'text-green-900 line-through' : 'text-gray-900'}`}>
                                      {task.title}
                                    </h4>
                                    <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                                  </div>
                                  <div className="flex gap-2 ml-4">
                                    <Badge className={priorityColors[task.priority]}>
                                      {task.priority === 'low' ? 'Faible' :
                                       task.priority === 'medium' ? 'Moyenne' :
                                       task.priority === 'high' ? 'Haute' : 'Urgente'}
                                    </Badge>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                  <span>👤 {task.employee_name}</span>
                                  {task.deadline && (
                                    <span>📅 Échéance: {new Date(task.deadline).toLocaleDateString('fr-FR')}</span>
                                  )}
                                  <span>Assignée par: {task.assigned_by_name}</span>
                                </div>

                                {isCompleted && (
                                  <p className="text-sm text-green-600 font-medium mt-2">
                                    ✓ Tâche complétée
                                  </p>
                                )}
                              </div>

                              {(user?.role === 'admin' || user?.role === 'employee') && (
                                <Button
                                  onClick={() => handleDeleteTask(task.id)}
                                  variant="destructive"
                                  size="sm"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  ) : (
                    <Card className="border-2 border-dashed border-gray-300 bg-white">
                      <CardContent className="p-8 text-center">
                        <Activity className="h-12 w-12 mx-auto mb-4 text-gray-400 opacity-50" />
                        <p className="text-gray-600">
                          Aucune tâche n'a encore été créée pour ce dossier.
                        </p>
                        {(user?.role === 'admin' || user?.role === 'employee') && (
                          <Button onClick={() => setTaskDialogOpen(true)} className="mt-4">
                            Créer la première tâche
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-6">
            <Card className="border-0 shadow-xl bg-gradient-to-br from-green-50 to-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-6 w-6 text-green-600" />
                  Échéancier de Paiements
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Statistiques des paiements */}
                {payments.length > 0 && (
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg border-2 border-green-200">
                      <p className="text-sm text-gray-600 mb-1">Total Payé</p>
                      <p className="text-2xl font-bold text-green-600">{getTotalPaid().toFixed(2)}$</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border-2 border-yellow-200">
                      <p className="text-sm text-gray-600 mb-1">Montant Restant</p>
                      <p className="text-2xl font-bold text-yellow-600">{getTotalPending().toFixed(2)}$</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border-2 border-blue-200">
                      <p className="text-sm text-gray-600 mb-1">Progression</p>
                      <p className="text-2xl font-bold text-blue-600">{getPaymentProgress()}%</p>
                    </div>
                  </div>
                )}

                {/* Barre de progression */}
                {payments.length > 0 && (
                  <div className="mb-6 p-4 bg-white rounded-lg border-2 border-green-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Progression des paiements</span>
                      <span className="text-xl font-bold text-green-600">{getPaymentProgress()}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-gradient-to-r from-green-600 to-emerald-600 h-4 rounded-full transition-all duration-500"
                        style={{ width: `${getPaymentProgress()}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      {payments.filter(p => p.status === 'paye').length} sur {payments.length} versement(s) payé(s)
                    </p>
                  </div>
                )}

                {/* Liste des paiements */}
                <div className="space-y-3">
                  {payments.length > 0 ? (
                    payments
                      .sort((a, b) => (a.installment_number || 0) - (b.installment_number || 0))
                      .map((payment, index) => {
                        const isPaid = payment.status === 'paye';
                        const isOverdue = payment.status === 'en_retard';
                        const dueDate = new Date(payment.due_date);

                        return (
                          <Card key={payment.id} className={`border-2 ${
                            isPaid ? 'border-green-500 bg-green-50' : 
                            isOverdue ? 'border-red-500 bg-red-50' : 
                            'border-yellow-300 bg-yellow-50'
                          }`}>
                            <CardContent className="p-4">
                              <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white flex items-center justify-center border-2 border-green-600 font-bold text-lg text-green-600">
                                  {payment.installment_number || index + 1}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                      <h4 className={`font-semibold ${isPaid ? 'text-green-900' : 'text-gray-900'}`}>
                                        {payment.description}
                                      </h4>
                                      {payment.notes && (
                                        <p className="text-xs text-gray-600 mt-1">{payment.notes}</p>
                                      )}
                                    </div>
                                    <div className="flex gap-2 ml-4">
                                      <Badge className={
                                        isPaid ? 'bg-green-100 text-green-800' :
                                        isOverdue ? 'bg-red-100 text-red-800' :
                                        'bg-yellow-100 text-yellow-800'
                                      }>
                                        {isPaid ? '✓ Payé' : isOverdue ? '⚠ En retard' : '⏳ En attente'}
                                      </Badge>
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                                    <div>
                                      <span className="text-gray-600">Montant:</span>
                                      <span className="font-bold text-green-600 ml-2">{payment.amount.toFixed(2)}$</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Échéance:</span>
                                      <span className={`font-medium ml-2 ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                                        {dueDate.toLocaleDateString('fr-FR')}
                                      </span>
                                    </div>
                                  </div>

                                  {isPaid && payment.payment_date && (
                                    <div className="mt-3 p-2 bg-green-100 rounded text-sm">
                                      <p className="text-green-800">
                                        ✓ Payé le {new Date(payment.payment_date).toLocaleDateString('fr-FR')}
                                        {payment.payment_method && ` par ${payment.payment_method}`}
                                        {payment.reference && ` (Réf: ${payment.reference})`}
                                      </p>
                                    </div>
                                  )}

                                  {!isPaid && (user?.role === 'admin' || user?.role === 'employee') && (
                                    <div className="mt-3">
                                      <Button
                                        onClick={() => handleMarkPaymentPaid(payment.id)}
                                        className="bg-green-600 hover:bg-green-700"
                                        size="sm"
                                      >
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Marquer comme payé
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })
                  ) : (
                    <Card className="border-2 border-dashed border-green-300 bg-white">
                      <CardContent className="p-8 text-center">
                        <DollarSign className="h-12 w-12 mx-auto mb-4 text-green-400 opacity-50" />
                        <p className="text-gray-600 mb-2">
                          Aucun échéancier de paiement n'a encore été généré pour ce dossier.
                        </p>
                        <p className="text-sm text-gray-500">
                          Les paiements seront générés automatiquement lors de l'approbation d'un devis.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            {/* Contrats et Entente Section */}
            <Card className="border-0 shadow-xl bg-gradient-to-br from-blue-50 to-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-6 w-6 text-blue-600" />
                  📝 Contrats et Entente
                </CardTitle>
                <p className="text-sm text-gray-600 mt-2">
                  Contrat Initial et Contrats de Service Professionnel pour ce dossier
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {documents.filter(doc => 
                    doc.is_contract && 
                    (doc.document_type === 'contrat_initial' || doc.document_type === 'contrat_service_professionnel')
                  ).length > 0 ? (
                    documents.filter(doc => 
                      doc.is_contract && 
                      (doc.document_type === 'contrat_initial' || doc.document_type === 'contrat_service_professionnel')
                    ).map((doc) => (
                      <Card key={doc.id} className="border-2 border-blue-200 bg-white">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4 flex-1">
                              <div className={`p-3 rounded-lg ${
                                doc.status === 'signed' ? 'bg-green-100' : 'bg-blue-100'
                              }`}>
                                <FileCheck className={`h-6 w-6 ${
                                  doc.status === 'signed' ? 'text-green-600' : 'text-blue-600'
                                }`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <p className="font-bold text-gray-900">
                                    {getContractName(doc.document_type)}
                                  </p>
                                  {doc.status === 'signed' ? (
                                    <Badge className="bg-green-100 text-green-800">
                                      ✓ Signé
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-yellow-100 text-yellow-800">
                                      En attente de signature
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mb-2">
                                  Généré le {safeFormatDate(doc.uploaded_at, 'dd/MM/yyyy à HH:mm')}
                                </p>
                                {doc.status === 'signed' && doc.signed_at && (
                                  <p className="text-sm text-green-600 font-medium">
                                    Signé le {safeFormatDate(doc.signed_at, 'dd/MM/yyyy à HH:mm')}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex flex-col gap-2 ml-4">
                              <Button
                                onClick={() => handleDownloadContract(doc.id, doc.name || doc.filename)}
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2"
                              >
                                <Download className="h-4 w-4" />
                                Télécharger
                              </Button>
                              
                              {user?.role === 'client' && doc.status !== 'signed' && (
                                <label>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    className="w-full bg-green-600 hover:bg-green-700"
                                    as="span"
                                  >
                                    <Upload className="h-4 w-4 mr-2" />
                                    Téléverser signé
                                  </Button>
                                  <input
                                    type="file"
                                    hidden
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => {
                                      if (e.target.files[0]) {
                                        handleUploadSignedContract(doc.id, e.target.files[0]);
                                      }
                                    }}
                                  />
                                </label>
                              )}
                              
                              {(user?.role === 'admin' || user?.role === 'employee') && (
                                <Button
                                  onClick={() => handleDeleteDocument(doc.id, doc.name || doc.filename)}
                                  variant="destructive"
                                  size="sm"
                                  className="flex items-center gap-2"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Supprimer
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card className="border-2 border-dashed border-blue-300 bg-white">
                      <CardContent className="p-8 text-center">
                        <FileCheck className="h-12 w-12 mx-auto mb-4 text-blue-400 opacity-50" />
                        <p className="text-gray-600">
                          Les contrats seront générés automatiquement lors de la création du dossier
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Devis et Facturation Section */}
            <Card className="border-0 shadow-xl bg-gradient-to-br from-green-50 to-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-6 w-6 text-green-600" />
                  💰 Devis et Facturation
                </CardTitle>
                <p className="text-sm text-gray-600 mt-2">
                  Devis, Factures et Reçus pour ce dossier
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {documents.filter(doc => 
                    doc.document_type === 'quote' || 
                    doc.document_type === 'invoice' || 
                    doc.document_type === 'recu' ||
                    doc.document_type === 'recu_paiement'
                  ).length > 0 ? (
                    documents.filter(doc => 
                      doc.document_type === 'quote' || 
                      doc.document_type === 'invoice' || 
                      doc.document_type === 'recu' ||
                      doc.document_type === 'recu_paiement'
                    ).map((doc) => (
                      <Card key={doc.id} className="border-2 border-green-200 bg-white">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4 flex-1">
                              <div className="p-3 rounded-lg bg-green-100">
                                <DollarSign className="h-6 w-6 text-green-600" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <p className="font-bold text-gray-900">
                                    {doc.document_type === 'quote' ? 'Devis' : 
                                     doc.document_type === 'invoice' ? 'Facture' : 
                                     'Reçu de Paiement'}
                                  </p>
                                  <Badge className="bg-green-100 text-green-800">
                                    {doc.document_type === 'quote' ? '📋 Devis' : 
                                     doc.document_type === 'invoice' ? '📄 Facture' : 
                                     '🧾 Reçu'}
                                  </Badge>
                                </div>
                                <p className="text-sm text-gray-600 mb-1">
                                  {doc.filename || doc.name || 'Document'}
                                </p>
                                <p className="text-sm text-gray-500">
                                  Généré le {safeFormatDate(doc.uploaded_at, 'dd/MM/yyyy à HH:mm')}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex flex-col gap-2 ml-4">
                              <Button
                                onClick={() => handleDownloadContract(doc.id, doc.name || doc.filename)}
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2"
                              >
                                <Download className="h-4 w-4" />
                                Télécharger
                              </Button>
                              
                              {(user?.role === 'admin' || user?.role === 'employee') && (
                                <Button
                                  onClick={() => handleDeleteDocument(doc.id, doc.name || doc.filename)}
                                  variant="destructive"
                                  size="sm"
                                  className="flex items-center gap-2"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Supprimer
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card className="border-2 border-dashed border-green-300 bg-white">
                      <CardContent className="p-8 text-center">
                        <DollarSign className="h-12 w-12 mx-auto mb-4 text-green-400 opacity-50" />
                        <p className="text-gray-600">
                          Aucun devis, facture ou reçu pour ce dossier
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Les documents financiers seront générés automatiquement lors des transactions
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Client Documents Section - Only visible to admin/employee */}
            {(user?.role === 'admin' || user?.role === 'employee') && (
              <Card className="border-0 shadow-xl bg-gradient-to-br from-orange-50 to-yellow-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <File className="h-6 w-6 text-orange-600" />
                    📁 Documents Clients
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-2">
                    Documents uploadés par le client, organisés par catégorie
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {documents.filter(doc => !doc.is_contract).length > 0 ? (
                      <div className="space-y-3">
                        <p className="text-sm text-gray-600 mb-4">
                          Tous les documents uploadés par le client, organisés par catégorie pour faciliter la révision.
                        </p>
                        {documents.filter(doc => !doc.is_contract).map((doc) => (
                          <Card key={doc.id} className="border-2 border-green-200 bg-white">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className={`p-2 rounded-lg ${
                                    doc.is_checked ? 'bg-green-100' : 'bg-gray-100'
                                  }`}>
                                    {doc.is_checked ? (
                                      <CheckCircle className="h-5 w-5 text-green-600" />
                                    ) : (
                                      <File className="h-5 w-5 text-gray-600" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-semibold text-gray-900">{doc.name}</p>
                                    <div className="flex items-center gap-3 text-sm text-gray-500">
                                      <span>Catégorie: {doc.category || 'Non catégorisé'}</span>
                                      <span>•</span>
                                      <span>Uploadé le {safeFormatDate(doc.uploaded_at, 'dd/MM/yyyy à HH:mm')}</span>
                                    </div>
                                    {doc.is_checked && (
                                      <p className="text-xs text-green-600 font-medium mt-1">✓ Document vérifié</p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={doc.is_checked || false}
                                    onChange={() => handleToggleCheck(doc.id, doc.is_checked)}
                                    className="w-4 h-4 text-green-600 rounded cursor-pointer"
                                    title="Marquer comme vérifié"
                                  />
                                  <Button
                                    onClick={() => handleDownloadContract(doc.id, doc.name)}
                                    variant="outline"
                                    size="sm"
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteDocument(doc.id, doc.name)}
                                    variant="destructive"
                                    size="sm"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <Card className="border-2 border-dashed border-green-300 bg-white">
                        <CardContent className="p-8 text-center">
                          <File className="h-12 w-12 mx-auto mb-4 text-green-400 opacity-50" />
                          <p className="text-gray-600">
                            Aucun document client n'a encore été uploadé pour ce dossier.
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Documents Catégorisés Section */}
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <File className="h-6 w-6 text-red-600" />
                  Documents du Dossier
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Documents List by Category */}
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900">Documents par Catégorie</h3>
                  
                  {Object.entries(DOCUMENT_CATEGORIES_CONTENT).map(([categoryKey, categoryData]) => {
                    const categoryDocs = getDocumentsByCategory(categoryKey);
                    const checkedCount = categoryDocs.filter(d => d.is_checked).length;
                    
                    return (
                      <div key={categoryKey} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                        {/* Category Header */}
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <span className="text-2xl">{categoryData.emoji}</span>
                              <div className="flex-1">
                                <p className="font-bold text-gray-900 mb-2">{categoryData.title}</p>
                                <div className="bg-white p-3 rounded-lg border border-blue-200 mb-2">
                                  <p className="text-sm font-semibold text-blue-800 mb-1">Documents attendus dans cette catégorie:</p>
                                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                                    {categoryData.content.map((item, idx) => (
                                      <li key={idx}>{item}</li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="flex gap-4 text-sm text-gray-600">
                                  <span>📄 {categoryDocs.length} document(s) téléversé(s)</span>
                                  {(user?.role === 'admin' || user?.role === 'employee') && (
                                    <span>✓ {checkedCount} vérifié(s)</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Badge className="ml-4">{categoryDocs.length}</Badge>
                          </div>
                        </div>
                        
                        {/* Category-specific Dropzone */}
                        <CategoryDropzone 
                          category={categoryKey}
                          onUpload={handleUploadForCategory}
                        />
                        
                        {/* Documents List */}
                        {categoryDocs.length > 0 ? (
                          <div className="p-4 space-y-3 bg-gray-50">
                            {categoryDocs.map(doc => (
                              <div key={doc.id} className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                                doc.is_checked ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
                              }`}>
                                {(user?.role === 'admin' || user?.role === 'employee') && (
                                  <input
                                    type="checkbox"
                                    checked={doc.is_checked || false}
                                    onChange={() => handleToggleCheck(doc.id, doc.is_checked)}
                                    className="w-5 h-5 text-green-600 rounded cursor-pointer"
                                  />
                                )}
                                <div className={`p-3 rounded-lg ${doc.is_checked ? 'bg-green-100' : 'bg-gray-100'}`}>
                                  {doc.is_checked ? <CheckCircle className="h-6 w-6 text-green-600" /> : <File className="h-6 w-6 text-gray-600" />}
                                </div>
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900">{doc.name}</p>
                                  <p className="text-sm text-gray-500">
                                    Téléversé le {safeFormatDate(doc.uploaded_at, 'dd/MM/yyyy à HH:mm')}
                                  </p>
                                  {doc.is_checked && (
                                    <p className="text-xs text-green-600 font-medium mt-1">✓ Document vérifié</p>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <Button onClick={() => handleDownloadContract(doc.id, doc.name)} variant="outline" size="sm">
                                    <Download className="h-4 w-4 mr-2" />Télécharger
                                  </Button>
                                  {(user?.role === 'admin' || user?.role === 'employee') && (
                                    <Button 
                                      onClick={() => handleDeleteDocument(doc.id, doc.name)} 
                                      variant="destructive" 
                                      size="sm"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-8 text-center text-gray-500 bg-gray-50">
                            <File className="h-12 w-12 mx-auto mb-3 opacity-30" />
                            <p className="text-sm">Aucun document dans cette catégorie</p>
                            <p className="text-xs text-gray-400 mt-1">Utilisez la zone de téléversement ci-dessus pour ajouter des documents</p>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Autres documents (non catégorisés ou catégorie "autres") */}
                  <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                    {/* Category Header */}
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <span className="text-2xl">📎</span>
                          <div className="flex-1">
                            <p className="font-bold text-gray-900 mb-2">Autres documents</p>
                            <div className="bg-white p-3 rounded-lg border border-gray-200 mb-2">
                              <p className="text-sm font-semibold text-gray-800 mb-1">Documents divers:</p>
                              <p className="text-sm text-gray-700">
                                Tous autres documents qui ne rentrent pas dans les catégories spécifiques ci-dessus.
                              </p>
                            </div>
                            <div className="flex gap-4 text-sm text-gray-600">
                              <span>📄 {documents.filter(doc => !doc.is_contract && doc.category === 'autres').length} document(s) téléversé(s)</span>
                              {(user?.role === 'admin' || user?.role === 'employee') && (
                                <span>✓ {documents.filter(doc => !doc.is_contract && doc.category === 'autres' && doc.is_checked).length} vérifié(s)</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Badge className="ml-4">{documents.filter(doc => !doc.is_contract && doc.category === 'autres').length}</Badge>
                      </div>
                    </div>
                    
                    {/* Category-specific Dropzone */}
                    <CategoryDropzone 
                      category="autres"
                      onUpload={handleUploadForCategory}
                    />
                    
                    {/* Documents List */}
                    {documents.filter(doc => !doc.is_contract && doc.category === 'autres').length > 0 ? (
                      <div className="p-4 space-y-3 bg-gray-50">
                        {documents.filter(doc => !doc.is_contract && doc.category === 'autres').map(doc => (
                          <div key={doc.id} className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                            doc.is_checked ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
                          }`}>
                            {(user?.role === 'admin' || user?.role === 'employee') && (
                              <input
                                type="checkbox"
                                checked={doc.is_checked || false}
                                onChange={() => handleToggleCheck(doc.id, doc.is_checked)}
                                className="w-5 h-5 text-green-600 rounded cursor-pointer"
                              />
                            )}
                            <div className={`p-3 rounded-lg ${doc.is_checked ? 'bg-green-100' : 'bg-gray-100'}`}>
                              {doc.is_checked ? <CheckCircle className="h-6 w-6 text-green-600" /> : <File className="h-6 w-6 text-gray-600" />}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{doc.name}</p>
                              <p className="text-sm text-gray-500">
                                Téléversé le {safeFormatDate(doc.uploaded_at, 'dd/MM/yyyy à HH:mm')}
                              </p>
                              {doc.is_checked && (
                                <p className="text-xs text-green-600 font-medium mt-1">✓ Document vérifié</p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button onClick={() => handleDownloadContract(doc.id, doc.name)} variant="outline" size="sm">
                                <Download className="h-4 w-4 mr-2" />Télécharger
                              </Button>
                              {(user?.role === 'admin' || user?.role === 'employee') && (
                                <Button 
                                  onClick={() => handleDeleteDocument(doc.id, doc.name)} 
                                  variant="destructive" 
                                  size="sm"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500 bg-gray-50">
                        <File className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">Aucun document dans cette catégorie</p>
                        <p className="text-xs text-gray-400 mt-1">Utilisez la zone de téléversement ci-dessus pour ajouter des documents</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Comments Tab */}
          <TabsContent value="comments" className="space-y-6">
            {/* Add Comment */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="space-y-3">
                  <Textarea
                    placeholder="Ajouter un commentaire..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={3}
                    data-testid="comment-input"
                  />
                  <Button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    data-testid="add-comment-btn"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Ajouter un commentaire
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Comments List */}
            <div className="space-y-4">
              {comments.map((comment) => (
                <Card key={comment.id} className="border-0 shadow-lg" data-testid={`comment-card-${comment.id}`}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">{comment.user_name}</p>
                        <Badge variant="outline" className="text-xs mt-1">
                          {comment.user_role === 'admin' ? 'Administrateur' : comment.user_role === 'employee' ? 'Employé' : 'Client'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">
                        {safeFormatDate(comment.created_at)}
                      </p>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                  </CardContent>
                </Card>
              ))}
              {comments.length === 0 && (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-12 text-center text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Aucun commentaire</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Activities Tab */}
          <TabsContent value="activities" className="space-y-4">
            {activities.map((activity) => (
              <Card key={activity.id} className="border-0 shadow-lg" data-testid={`activity-card-${activity.id}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-semibold text-gray-900">{activity.user_name}</p>
                    <p className="text-sm text-gray-500">
                      {safeFormatDate(activity.timestamp)}
                    </p>
                  </div>
                  <p className="text-gray-600">{activity.description}</p>
                  <Badge className="mt-2" variant="outline">
                    {activity.action}
                  </Badge>
                </CardContent>
              </Card>
            ))}
            {activities.length === 0 && (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center text-gray-500">
                  <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t('no_data')}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Dialogue de création de tâche */}
        <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Créer une nouvelle tâche</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Employé assigné *</Label>
                <Select value={newTask.employee_id} onValueChange={(value) => setNewTask({...newTask, employee_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un employé" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Titre de la tâche *</Label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  placeholder="Ex: Vérifier les documents d'identité"
                />
              </div>

              <div>
                <Label>Description</Label>
                <textarea
                  className="w-full border rounded-md p-2"
                  rows="3"
                  value={newTask.description}
                  onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                  placeholder="Détails de la tâche..."
                />
              </div>

              <div>
                <Label>Priorité</Label>
                <Select value={newTask.priority} onValueChange={(value) => setNewTask({...newTask, priority: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Faible</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="high">Haute</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Date d'échéance</Label>
                <Input
                  type="date"
                  value={newTask.deadline}
                  onChange={(e) => setNewTask({...newTask, deadline: e.target.value})}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleCreateTask}>
                  Créer la tâche
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default FileDetail;